import React, { useState, useEffect, useRef, ReactNode, CSSProperties, useCallback } from 'react';
import './index.scss';

interface SimpleSwiperProps {
  /**
   * 轮播内容
   */
  children?: ReactNode;
  
  /**
   * 轮播方向
   * @default 'horizontal'
   */
  direction?: 'horizontal' | 'vertical';
  
  /**
   * 是否自动播放
   * @default false
   */
  autoplay?: boolean;
  
  /**
   * 是否循环
   * @default false
   */
  loop?: boolean;
  
  /**
   * 自动播放间隔（毫秒）
   * @default 3000
   */
  autoplayInterval?: number;
  
  /**
   * 索引变化回调
   */
  onIndexChange?: (index: number) => void;
  
  /**
   * 自定义样式
   */
  style?: CSSProperties;
}

/**
 * 简化版轮播组件
 * 实现真正的无缝循环轮播
 */
const SimpleSwiper: React.FC<SimpleSwiperProps> = ({
  children,
  direction = 'horizontal',
  autoplay = false,
  loop = false,
  autoplayInterval = 3000,
  onIndexChange,
  style,
}) => {
  const items = React.Children.toArray(children);
  const count = items.length;
  
  // 实际的 DOM 索引（包括克隆元素）
  const [currentIndex, setCurrentIndex] = useState(loop && count > 1 ? 1 : 0);
  // 是否启用过渡动画
  const [hasTransition, setHasTransition] = useState(true);
  
  const timerRef = useRef<number | null>(null);
  const onIndexChangeRef = useRef(onIndexChange);
  const isVisibleRef = useRef(true);
  
  // 更新回调引用
  useEffect(() => {
    onIndexChangeRef.current = onIndexChange;
  }, [onIndexChange]);
  
  // 获取真实索引（用于回调）
  const getRealIndex = useCallback((index: number) => {
    if (!loop || count <= 1) return index;
    if (index === 0) return count - 1;
    if (index === count + 1) return 0;
    return index - 1;
  }, [loop, count]);
  
  // 切换到下一张
  const goNext = useCallback(() => {
    if (count <= 1) return;
    setHasTransition(true);
    setCurrentIndex(prev => prev + 1);
  }, [count]);
  
  // 处理过渡结束
  const handleTransitionEnd = useCallback(() => {
    if (!loop || count <= 1) return;
    
    // 滚动到克隆的第一张（最右边），瞬间跳到真实的第一张
    if (currentIndex === count + 1) {
      setHasTransition(false);
      // 使用 setTimeout 确保状态更新完成
      setTimeout(() => {
        setCurrentIndex(1);
      }, 0);
    }
    // 滚动到克隆的最后一张（最左边），瞬间跳到真实的最后一张
    else if (currentIndex === 0) {
      setHasTransition(false);
      setTimeout(() => {
        setCurrentIndex(count);
      }, 0);
    }
  }, [loop, count, currentIndex]);
  
  // 通知外部索引变化
  useEffect(() => {
    const realIndex = getRealIndex(currentIndex);
    setTimeout(() => {
      onIndexChangeRef.current?.(realIndex);
    }, 0);
  }, [currentIndex, getRealIndex]);
  
  // 启动/停止自动播放
  const startAutoplay = useCallback(() => {
    // 清除旧定时器
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (!autoplay || count <= 1 || !isVisibleRef.current) {
      return;
    }
    
    // 启动新定时器
    timerRef.current = window.setInterval(() => {
      if (isVisibleRef.current) {
        goNext();
      }
    }, autoplayInterval);
  }, [autoplay, autoplayInterval, count, goNext]);

  // 停止自动播放
  const stopAutoplay = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 自动播放
  useEffect(() => {
    startAutoplay();
    
    return () => {
      stopAutoplay();
    };
  }, [startAutoplay, stopAutoplay]);

  // 监听页面可见性变化
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isVisibleRef.current = isVisible;
      
      if (isVisible) {
        // 页面重新可见时，恢复自动播放并确保状态正确
        if (autoplay && count > 1) {
          // 确保过渡动画状态正确
          setHasTransition(true);
          
          // 检查并修复当前索引，确保在有效范围内
          setCurrentIndex(prev => {
            // 如果当前索引无效，重置到初始位置
            if (loop && count > 1) {
              if (prev < 1 || prev > count) {
                return 1;
              }
            } else {
              if (prev < 0 || prev >= count) {
                return 0;
              }
            }
            return prev;
          });
          
          // 延迟启动，确保状态更新完成
          setTimeout(() => {
            startAutoplay();
          }, 100);
        }
      } else {
        // 页面进入后台时，停止自动播放
        stopAutoplay();
      }
    };

    // 监听 visibilitychange 事件
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // 也监听 pageshow 事件（用于某些浏览器的恢复场景）
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        // 页面从缓存恢复
        handleVisibilityChange();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [autoplay, count, loop, startAutoplay, stopAutoplay]);
  
  if (count === 0) {
    return null;
  }
  
  // 构建渲染列表
  const renderItems = loop && count > 1
    ? [items[count - 1], ...items, items[0]]
    : items;
  
  // 计算偏移量
  const offset = -currentIndex * 100;
  const transform = direction === 'vertical' 
    ? `translate3d(0, ${offset}%, 0)` 
    : `translate3d(${offset}%, 0, 0)`;
  
  return (
    <div 
      className={`simple-swiper simple-swiper-${direction}`}
      style={style}
    >
      <div 
        className="simple-swiper-track"
        style={{
          transform,
          transition: hasTransition ? 'transform 300ms ease-out' : 'none',
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {renderItems.map((item, index) => (
          <div key={`slide-${index}`} className="simple-swiper-slide">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

// Item 组件
const SwiperItem: React.FC<{ children?: ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// 定义复合组件类型
type SimpleSwiperComponent = React.FC<SimpleSwiperProps> & {
  Item: typeof SwiperItem;
};

// 添加 Item 属性
const SimpleSwiperWithItem = SimpleSwiper as SimpleSwiperComponent;
SimpleSwiperWithItem.Item = SwiperItem;

export default SimpleSwiperWithItem;
